import { count, listed, plural } from './format.js';
import { age, groupBy, hasItems, insideOf, kindOf } from './inside.js';
import { isEvaluation } from './model.js';
import { THRESHOLDS } from './thresholds.js';
/*
 * Two checks over what IM-205 reads from the Kubernetes API, added with the
 * Prometheus checks of IM-207 because the story asked for them there: Kueue's
 * admission wait, and an object whose spec something keeps rewriting. Kueue's
 * own metrics would have answered the first from Prometheus, but the installer
 * does not scrape Kueue (es9kp25n, 2026-11-02), and its Workloads carry the
 * same times; generations are on every object and nowhere in Prometheus for
 * the kinds that matter, KEDA's.
 */
const Q = THRESHOLDS.queue;
const R = THRESHOLDS.rewrites;
const minutesBetween = (from, to) => from === undefined ? undefined : Math.max(0, (Date.parse(to) - Date.parse(from)) / 60_000);
const isTrue = (w, type) => w.conditions.find((c) => c.type === type && c.status === 'True');
/** How long each workload waited for admission, or has waited so far. Finished and unadmitted is neither. */
const waitsOf = (workloads, at) => workloads.flatMap((workload) => {
    const admitted = isTrue(workload, 'Admitted');
    if (admitted !== undefined) {
        const minutes = minutesBetween(workload.createdAt, admitted.since ?? at);
        return minutes === undefined ? [] : [{ workload, minutes, pending: false }];
    }
    if (isTrue(workload, 'Finished') !== undefined)
        return [];
    const minutes = minutesBetween(workload.createdAt, at);
    return minutes === undefined ? [] : [{ workload, minutes, pending: true }];
});
const waited = (minutes) => age(minutes / 60);
/** A ClusterQueue's quota against its use, per resource: what its next admission waits under. */
const quotaOf = (queue) => listed(queue.flavors.flatMap((flavor) => flavor.resources.map((r) => {
    const used = queue.usage
        .find((u) => u.flavor === flavor.flavor)
        ?.resources.find((u) => u.name === r.name)?.total;
    return `${r.name} ${used ?? '0'} of ${r.nominalQuota ?? 'no quota'}`;
})));
/**
 * Whether Kueue admits jobs promptly (IM-207).
 *
 * Kueue holds a job until its ClusterQueue has quota for it, so a sync that
 * waits there starts late, and one still waiting when the next run of its rule
 * arrives has fallen a whole interval behind. Measured from each Workload's
 * creation to its `Admitted` condition, or to now for one still waiting; the
 * ClusterQueue's quota and use are the evidence, because a queue at its
 * nominal quota admits the next job only when one finishes.
 */
export const queueAdmissionCheck = {
    id: 'queue-admission',
    title: 'Queued jobs are admitted promptly',
    category: 'capacity',
    outcomes: ['ok', 'warn', 'fail', 'unknown', 'not-applicable'],
    evaluate: (facts) => {
        const inside = insideOf(facts);
        if (isEvaluation(inside))
            return inside;
        const workloadsRead = kindOf(inside.value, 'workloads', 'Kueue’s workloads');
        if (!hasItems(workloadsRead))
            return workloadsRead;
        const queuesRead = kindOf(inside.value, 'clusterQueues', 'Kueue’s cluster queues');
        const clusterQueues = hasItems(queuesRead) ? queuesRead.items : [];
        const localRead = kindOf(inside.value, 'localQueues', 'Kueue’s local queues');
        const localQueues = hasItems(localRead) ? localRead.items : [];
        const waits = waitsOf(workloadsRead.items, facts.at);
        if (waits.length === 0) {
            return { outcome: 'ok', finding: 'No job is waiting for Kueue to admit it.', evidence: [] };
        }
        const queueOf = (w) => `${w.namespace}/${w.queue ?? 'no queue'}`;
        const evidence = [...groupBy(waits, (w) => queueOf(w.workload))].map(([queue, group]) => {
            const pending = group.filter((w) => w.pending);
            const admitted = group.filter((w) => !w.pending);
            const longest = (ws) => Math.max(...ws.map((w) => w.minutes));
            const first = group[0]?.workload;
            const cq = localQueues.find((l) => l.namespace === first?.namespace && l.name === first.queue)?.clusterQueue;
            const quota = clusterQueues.find((c) => c.name === cq);
            return {
                label: `Queue ${queue}`,
                value: [
                    pending.length === 0
                        ? undefined
                        : `${plural(pending.length, 'job')} waiting, the longest ${waited(longest(pending))}`,
                    admitted.length === 0
                        ? undefined
                        : `${plural(admitted.length, 'job')} admitted, the longest wait ${waited(longest(admitted))}`,
                    quota === undefined ? undefined : `cluster queue ${quota.name}: ${quotaOf(quota)}`,
                ]
                    .filter((part) => part !== undefined)
                    .join('; '),
            };
        });
        const worst = [...waits].sort((a, b) => b.minutes - a.minutes)[0];
        if (worst === undefined || worst.minutes < Q.admissionWarnMinutes) {
            return {
                outcome: 'ok',
                finding: `Kueue admitted every job within ${String(Q.admissionWarnMinutes)} minutes.`,
                evidence,
            };
        }
        const recommendation = 'Compare the cluster queue’s quota with what its jobs request: at its nominal quota it admits the next job only when one finishes. Raise the quota, or lower what each job requests.';
        const queue = queueOf(worst.workload);
        if (worst.pending && worst.minutes >= Q.admissionFailMinutes) {
            return {
                outcome: 'fail',
                finding: `A job has waited ${waited(worst.minutes)} for admission to ${queue}, past the ${String(Q.admissionFailMinutes)} minutes between dataset syncs, so each sync falls further behind.`,
                recommendation,
                evidence,
            };
        }
        return {
            outcome: 'warn',
            finding: `A job ${worst.pending ? 'has waited' : 'waited'} ${waited(worst.minutes)} for admission to ${queue}.`,
            recommendation,
            evidence,
        };
    },
};
/** The kinds a controller might keep rewriting, with the name a person knows them by. */
const REWRITTEN_KINDS = [
    ['deployments', 'Deployment'],
    ['statefulSets', 'StatefulSet'],
    ['daemonSets', 'DaemonSet'],
    ['horizontalPodAutoscalers', 'HorizontalPodAutoscaler'],
    ['scaledObjects', 'ScaledObject'],
    ['scaledJobs', 'ScaledJob'],
    ['nodePools', 'NodePool'],
];
const median = (values) => {
    const sorted = [...values].sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)] ?? 0;
};
/**
 * Whether something keeps rewriting an object (IM-207).
 *
 * Every change to an object's spec adds one to its `metadata.generation`, so
 * one far above every other of its kind is being changed continuously — two
 * controllers that disagree about its spec, or a script on a loop. That is
 * worth knowing before anyone edits it, because a change made by hand will be
 * overwritten within the minute. 24/7 NV's chunk-to-ES ScaledObject was at
 * 776,000.
 */
export const controllerRewritesCheck = {
    id: 'controller-rewrites',
    title: 'Nothing keeps rewriting an object',
    category: 'cluster',
    outcomes: ['ok', 'warn', 'unknown', 'not-applicable'],
    evaluate: (facts) => {
        const inside = insideOf(facts);
        if (isEvaluation(inside))
            return inside;
        const unread = [];
        const flagged = [];
        let first;
        for (const [id, kind] of REWRITTEN_KINDS) {
            const read = inside.value.kinds[id];
            if (read.state === 'unreadable')
                unread.push(kind);
            if (read.state !== 'read')
                continue;
            const items = read.items;
            const generations = items.flatMap((i) => (i.generation === undefined ? [] : [i.generation]));
            const typical = Math.max(1, median(generations));
            for (const item of items) {
                const generation = item.generation ?? 0;
                if (generation < R.minGeneration || generation < typical * R.medianRatio)
                    continue;
                const name = item.namespace === undefined ? item.name : `${item.namespace}/${item.name}`;
                first ??= `${kind} ${name} is at generation ${count(generation)}`;
                flagged.push({
                    label: `${kind} ${name}`,
                    value: `generation ${count(generation)}; the median ${kind} here is at ${count(typical)}`,
                });
            }
        }
        if (unread.length === REWRITTEN_KINDS.length) {
            return {
                outcome: 'unknown',
                finding: 'Could not read any of the kinds a controller rewrites.',
                evidence: [],
            };
        }
        const partial = unread.length === 0 ? [] : [{ label: 'Not read', value: listed(unread) }];
        if (first !== undefined) {
            return {
                outcome: 'warn',
                finding: `${first}: something changes its spec continuously, and a change made by hand will be overwritten.${flagged.length > 1 ? ` ${plural(flagged.length - 1, 'other object')} too.` : ''}`,
                recommendation: 'Find what is changing it — an operator, a script, or two controllers that disagree about its spec — before changing it by hand. The continuous writes also load the cluster’s API.',
                evidence: [...flagged, ...partial],
            };
        }
        return {
            outcome: 'ok',
            finding: 'No object’s spec changes far more often than others of its kind.',
            evidence: partial,
        };
    },
};
//# sourceMappingURL=controllers.js.map